/**
 * calcFullPrice utility (SPEC-035: PDP Add-on Options with Pricing)
 *
 * Ported from Angular widget calcFullPrice utility.
 * Computes the total price = base variant price + sum of selected add-on option prices.
 */

import type { ProductDetailProductOption } from '../types/api';

/**
 * Extracts the numeric amount and currency symbol from a price string.
 *
 * Examples:
 *   extractMoney("$1,299.00") => { amount: 1299.00, currency: "$" }
 *   extractMoney("1,299.00")  => { amount: 1299.00, currency: "" }
 *   extractMoney("$25.00")    => { amount: 25.00, currency: "$" }
 *   extractMoney("$0.00")     => { amount: 0, currency: "$" }
 */
export function extractMoney(priceString: string): { amount: number; currency: string } {
  const cleaned = priceString.replace(/,/g, '');
  const match = cleaned.match(/[0-9]+([.][0-9]+)?/);
  const amount = match ? parseFloat(match[0]) : 0;
  // Currency symbol is the non-numeric, non-dot, non-comma part
  const currency = priceString.replace(/[0-9,. ]+/g, '').trim();
  return { amount, currency };
}

/**
 * Rounds a decimal value to 2 decimal places, then returns the result.
 * Note: Matches Angular widget behavior — integer results have no trailing ".00".
 */
export function roundDecimal(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Computes the full price as base variant price + sum of selected product option prices.
 *
 * @param basePrice - Base variant price string (e.g., "$1,299.00" or "1,299.00")
 * @param allOptions - All ProductDetailProductOption entries for the current variant
 * @param selectedOptions - Map of { [identifier]: selectedValue }
 * @returns { priceStr: string, priceNum: number }
 *
 * Examples (matching Angular widget roundDecimal behavior — no ".00" suffix on integers):
 *   calcFullPrice("$1,299.00", options, { engraving: "initials" })
 *     => { priceStr: "$1,324", priceNum: 1324 }   (1299 + 25 = 1324)
 *   calcFullPrice("$1,299.00", options, {})
 *     => { priceStr: "$1,299", priceNum: 1299 }
 *   calcFullPrice("$1,299.00", [], {})
 *     => { priceStr: "$1,299", priceNum: 1299 }
 */
export function calcFullPrice(
  basePrice: string,
  allOptions: ProductDetailProductOption[] | undefined,
  selectedOptions: Record<string, string>
): { priceStr: string; priceNum: number } {
  const { amount: baseAmount, currency } = extractMoney(basePrice);

  const optionsToSum = (allOptions ?? []).filter(
    (option) => selectedOptions[option.identifier] === option.value
  );

  const optionTotal = optionsToSum.reduce((sum, option) => {
    const { amount } = extractMoney(option.price);
    return sum + amount;
  }, 0);

  const total = roundDecimal(baseAmount + optionTotal);

  // Format with thousand commas, no trailing ".00" for integers (matches Angular widget behavior).
  // e.g., 1324 => "1,324", 1324.5 => "1,324.5"
  const formattedTotal = Number.isInteger(total)
    ? total.toLocaleString('en-US', { maximumFractionDigits: 0 })
    : total.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return {
    priceStr: `${currency}${formattedTotal}`,
    priceNum: total,
  };
}
